/**
 * The stores: what the airport holds on the shelf, what is reserved against a
 * job, what is on order, and what is about to run out.
 *
 * ── why an airport's stores are not a warehouse ─────────────────────────
 *
 * A generic inventory system answers "how much is on the shelf" and stops
 * there. An aerodrome needs three answers that question does not reach:
 *
 *  - **What is this part for?** An isolating transformer is not stock, it is
 *    the difference between a lit runway and a closed one. Every line here
 *    carries what it serves — an asset type, a fleet kind, a trade — so the
 *    question "can we fix that machine" is answerable from the register
 *    rather than from somebody's memory of the shelf.
 *  - **Is anything waiting on it?** A part with four on the shelf and five
 *    reserved against open work orders is out of stock, and a system that
 *    reports it as "4 in stock" sends a technician to an empty bin. On hand,
 *    reserved and available are three different numbers and this file never
 *    conflates them.
 *  - **Is it critical?** A critical spare is one whose absence stops an
 *    operation — a runway lamp, a CCR card, a fire appliance pump seal. It is
 *    held to a different rule from a box of gloves, and "critical" here
 *    carries the reason, because a criticality nobody can justify is the
 *    first thing a cost review deletes.
 *
 * ── the three numbers, kept apart ───────────────────────────────────────
 *
 *   on hand   — counted, in a bin, in a store
 *   reserved  — promised to an open work order, still on the shelf
 *   available — on hand minus reserved: what the next job can actually have
 *
 * and beside them `on order`, which is none of the three: it is a promise
 * from a supplier, and a promise is not stock. It is shown, never added in.
 *
 * ── what is unknown stays unknown ───────────────────────────────────────
 *
 * Days of cover is stock divided by the rate it is used. An item nobody has
 * ever drawn has no rate, and dividing by zero gives infinity, which reads on
 * a screen as "we have plenty" — the most confident possible way to be wrong
 * about a part nobody has needed yet. Cover for such an item is `null` and
 * the screen says so. The same goes for an item never counted: its accuracy
 * is unknown, not 100%.
 *
 * Pure. No React, no fetch, no clock of its own — `now` is passed in.
 */
import { t, tn } from "../i18n";

/* ── what the airport holds ───────────────────────────────────────────── */

/**
 * A category key.
 *
 * Not a fixed union. The thirteen below are what this platform ships with and
 * what the executive Logistics board names, with the same keys, so what this
 * register computes reaches that board without a translation in between — but
 * a store that holds something none of them describes must be able to say so.
 * An airport that keeps a separate shelf for de-icing chemicals, or for the
 * baggage reconciliation system's consumables, should not have to file them
 * under "General consumables" and lose them.
 *
 * So the type is a string and the LIST is data: the built-ins, plus whatever
 * the storekeeper has added, held on the register beside the stock itself.
 */
export type StoreCategory = string;

export interface CategoryDef {
  key: StoreCategory;
  label: string;
  what: string;
  /** true for one the storekeeper added, which is also the only kind that can be removed */
  added?: boolean;
  /** who added it and when, because a category is a decision somebody made */
  by?: string;
  at?: string;
}

export const STORE_CATEGORIES: CategoryDef[] = [
  /*
   * AGL and Mechanical are the store's own trades, named by the airport.
   * They sit FIRST because an airfield store is organised around what stops
   * an operation, and an unlit runway stops one tonight.
   *
   * They are additions, not replacements: the seven categories that were here
   * are all still held, because a store that holds AGL parts also holds
   * gloves, and nothing was taken off the shelf to make room.
   *
   * AGL is kept apart from Electrical on purpose. A runway lamp and a
   * distribution board breaker are both electrical to an electrician and
   * entirely different to an aerodrome: one is a certified airfield lighting
   * component under the circuit's own regime, the other is building services.
   * Filing them together is how a store reports plenty of "electrical spares"
   * while holding no lamps.
   */
  { key: "agl", label: "AGL and airfield lighting", what: "runway and taxiway lamps, isolating transformers, CCR parts, series circuit cable, signs" },
  { key: "mechanical", label: "Mechanical", what: "pumps, valves, seals, bearings, drives, HVAC and plant mechanical parts" },
  { key: "consumable", label: "General consumables", what: "gloves, rags, fixings, sealant, cleaning" },
  { key: "electrical", label: "Electrical spares", what: "building electrical: breakers, cable, distribution, small power and lighting" },
  { key: "civil", label: "Civil maintenance", what: "asphalt, joint sealant, aggregate, concrete repair" },
  { key: "equipment", label: "Equipment and plant", what: "filters, brushes, hydraulic parts, tires, belts" },
  { key: "tools", label: "Tools", what: "hand and power tools, torque wrenches, test sets" },
  { key: "bhs", label: "BHS system parts", what: "belts, rollers, motors, photo-eyes, scanners" },
  { key: "utilities", label: "Utilities parts", what: "pump seals, valves, chiller parts, filters" },
  { key: "building", label: "Buildings and infrastructure", what: "doors, glazing, ceiling, floor, sanitary" },
  { key: "paint", label: "Paints and markings", what: "marking paint, glass beads, primer, thinner" },
  { key: "fencing", label: "Fencing and perimeter", what: "fence panels, razor tape, gate parts, posts" },
  { key: "electromech", label: "Electro-mechanical parts", what: "actuators, contactors, bearings, drives" },
];

/** The built-ins only. Prefer `categoriesOf(register)`, which includes the store's own. */
export const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  STORE_CATEGORIES.map((c) => [c.key, c.label]),
);

/** Every category this store files under: the built-ins, then its own. */
export function categoriesOf(register: Pick<StoresRegister, "categories">): CategoryDef[] {
  const own = (register.categories ?? []).filter((c) => !STORE_CATEGORIES.some((b) => b.key === c.key));
  return [...STORE_CATEGORIES, ...own.map((c) => ({ ...c, added: true }))];
}

/**
 * What to call a category.
 *
 * A key with no definition anywhere is shown AS the key rather than as a
 * blank: a line filed under something nobody declared is still a line, and a
 * register that renders it as an empty cell has hidden it.
 */
export function categoryLabel(key: StoreCategory, register?: Pick<StoresRegister, "categories">): string {
  if (register) {
    const found = categoriesOf(register).find((c) => c.key === key);
    if (found) return found.label;
  }
  return CATEGORY_LABEL[key] ?? key;
}

/** Where stock physically is. An airport's stores are not one room. */
export interface StoreLocation {
  code: string;
  name: string;
  /** main store, a satellite by the work, a van stocked for a crew, or a sealed bonded store */
  kind: "main" | "satellite" | "van" | "bonded";
  /** the department that holds the key */
  custodian?: string;
}

/** A quantity of one line, in one bin, in one store. */
export interface Bin {
  location: string;
  bin?: string;
  onHand: number;
}

/**
 * What a line serves, so the register answers "can we fix that".
 *
 * `assetTypes` are the platform's own asset type keys where the pack declares
 * them; `fleetKinds` are the equipment register's kinds; `trades` are the
 * facility-inspection trades. All three are optional, and a line that serves
 * nothing named is stated as general rather than guessed at.
 */
export interface Serves {
  assetTypes?: string[];
  fleetKinds?: string[];
  trades?: string[];
}

export interface StockItem {
  id: string;
  /** the stores catalogue number */
  sku: string;
  name: string;
  category: StoreCategory;
  /** unit of measure, as the storekeeper issues it: each, m, kg, litre, roll */
  uom: string;
  /** what one costs, in the register's currency */
  unitCost?: number;
  manufacturer?: string;
  partNumber?: string;
  /**
   * The barcode on the part or the bin, where it is not the catalogue number.
   *
   * A manufacturer's own symbol, or a label the store printed. Held separately
   * because a scanner reads whatever is physically stuck to the shelf, and
   * insisting that be the catalogue number would mean relabelling a store
   * before any of this could be used.
   */
  barcode?: string;
  /** reorder point and the level it is brought back to */
  min: number;
  max: number;
  /** working days from order to delivery, as experienced rather than quoted */
  leadDays?: number;
  /**
   * A critical spare, WITH the reason. A criticality with no reason attached
   * is deleted by the first cost review that reads it.
   */
  critical?: { why: string };
  serves?: Serves;
  supplier?: string;
  /** the contract in the stakeholder register this is bought under, where there is one */
  contractRef?: string;
  bins: Bin[];
  /** the last physical count: when, who, and what the difference was */
  counted?: { at: string; by: string; variance: number };
  hazardous?: boolean;
  /** shelf life matters for paint, sealant and beads; an expired tin is not stock */
  expiresAt?: string;
  note?: string;
  /** off the working catalogue, kept with its movements */
  obsoleted?: { at: string; by: string; reason: string };
}

/* ── what happens to it ───────────────────────────────────────────────── */

export type MovementKind = "receipt" | "issue" | "return" | "adjust" | "transfer" | "count";

/**
 * One movement of one line. `qty` is signed as the shelf sees it: a receipt is
 * positive, an issue negative, a return positive, an adjustment either way.
 *
 * Append-only. A correction is another movement, never an edit — the whole
 * value of a stock record is that it cannot be quietly revised to agree with
 * the shelf.
 */
export interface Movement {
  id: string;
  at: string;
  sku: string;
  kind: MovementKind;
  qty: number;
  location: string;
  bin?: string;
  by: string;
  /** the work order the parts went to, where they went to one */
  workOrder?: string;
  /** a purchase order, a delivery note, a count sheet */
  reference?: string;
  /** where a transfer went to, or came from */
  otherLocation?: string;
  unitCost?: number;
  note?: string;
}

/**
 * Stock promised to an open job.
 *
 * A reservation is the difference between a stores system and a list of
 * numbers. It is what makes "available" mean anything, and it is what lets a
 * planner know on Monday that Thursday's job cannot start.
 */
export interface Reservation {
  id: string;
  sku: string;
  qty: number;
  /** the work order's human number, e.g. WO-0042 */
  workOrder: string;
  at: string;
  by: string;
  /** the date the job needs it */
  neededAt?: string;
  /** how much of the reservation has actually been issued */
  issued: number;
  /** released without being issued: the job changed or was cancelled */
  released?: { at: string; by: string; reason: string };
}

/* ── buying it ────────────────────────────────────────────────────────── */

export type RequisitionStatus = "draft" | "submitted" | "approved" | "rejected" | "ordered" | "cancelled";

export interface RequisitionLine {
  sku: string;
  qty: number;
  /** replenishment, or a part for a named job */
  why: string;
  workOrder?: string;
  estimatedUnitCost?: number;
}

/** One step of the approval chain, with the authority that allowed it. */
export interface Approval {
  at: string;
  by: string;
  role: string;
  decision: "approved" | "rejected";
  /** the value limit of the authority used, in the register's currency; null = no limit */
  limit: number | null;
  note?: string;
}

export interface Requisition {
  id: string;
  number: string;
  raisedAt: string;
  raisedBy: string;
  department?: string;
  lines: RequisitionLine[];
  status: RequisitionStatus;
  approvals: Approval[];
  /** the purchase order it became */
  orderNumber?: string;
  note?: string;
}

export type OrderStatus = "open" | "part_received" | "received" | "cancelled";

export interface OrderLine {
  sku: string;
  qty: number;
  unitCost: number;
  /** cumulative, across every receipt against this line */
  received: number;
}

export interface Receipt {
  at: string;
  by: string;
  /** the supplier's delivery note */
  deliveryNote?: string;
  lines: { sku: string; qty: number; location: string; bin?: string }[];
  /** what was rejected at the gate and why — a rejected delivery is not stock */
  rejected?: { sku: string; qty: number; why: string }[];
}

export interface PurchaseOrder {
  id: string;
  number: string;
  supplier: string;
  contractRef?: string;
  raisedAt: string;
  raisedBy: string;
  /** what the supplier promised */
  expectedAt?: string;
  status: OrderStatus;
  lines: OrderLine[];
  receipts: Receipt[];
  requisitionNumber?: string;
  currency?: string;
  /**
   * An order somebody raised in another system — procurement's own, or the
   * airport's ERP — recorded here so its delivery can be booked in against
   * something. Named, because "who raised this" is the first question asked
   * about an order nobody here can find.
   */
  raisedElsewhere?: { system: string; reference?: string };
}

export interface StoresRegister {
  currency: string;
  locations: StoreLocation[];
  /** categories the storekeeper added, beyond the ones the platform ships with */
  categories?: CategoryDef[];
  items: StockItem[];
  movements: Movement[];
  reservations: Reservation[];
  requisitions: Requisition[];
  orders: PurchaseOrder[];
  enteredBy?: string;
  enteredAt?: string;
}

/* ── reading the register ─────────────────────────────────────────────── */

const DAY = 86_400_000;

function at(date: string | undefined): number | null {
  if (!date) return null;
  const ms = Date.parse(date.length <= 10 ? `${date}T00:00:00Z` : date);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Days until a date, negative when past; null when there is no date.
 *
 * Counted in CALENDAR days — both ends floored to midnight — rather than in
 * elapsed milliseconds. Elapsed time floored gets both directions wrong by a
 * day whenever the clock is not at midnight: a delivery due on Friday read at
 * nine on Monday morning becomes "in 3 days" instead of 4, and one that was
 * due six days ago becomes "7 days ago". Neither is a rounding preference.
 * Both are sentences the screen puts in front of somebody planning a job, and
 * a day either way is the difference between a crew turning up and not.
 */
export function daysUntil(date: string | undefined, now: Date): number | null {
  const ms = at(date);
  if (ms === null) return null;
  const midnight = (t: number) => Math.floor(t / DAY) * DAY;
  return Math.round((midnight(ms) - midnight(now.getTime())) / DAY);
}

/** Everything in every bin. */
export function onHand(item: StockItem): number {
  return item.bins.reduce((n, b) => n + b.onHand, 0);
}

/**
 * What is held at one store.
 *
 * A quantity is always a quantity somewhere. A line kept in the main store
 * and in a van has two figures and no single one of them is "the stock", so
 * anything that reads or writes a quantity against a place uses this and not
 * `onHand` — see storesIntake, where confusing the two once turned a count of
 * 58 into a register holding 122.
 */
export function heldAt(item: StockItem, location: string): number {
  return item.bins.filter((b) => b.location === location).reduce((n, b) => n + b.onHand, 0);
}

/** A reservation still owed: promised, not yet issued, not released. */
export function isOutstanding(r: Reservation): boolean {
  return !r.released && r.issued < r.qty;
}

/** Promised to open jobs and still on the shelf. */
export function reserved(sku: string, register: StoresRegister): number {
  return register.reservations
    .filter((r) => r.sku === sku && isOutstanding(r))
    .reduce((n, r) => n + (r.qty - r.issued), 0);
}

/**
 * What the next job can actually have.
 *
 * Never negative for display purposes? No — it IS allowed to go negative, and
 * that is the point: more promised than held is a real condition, and rounding
 * it up to zero hides the over-commitment that caused it.
 */
export function available(item: StockItem, register: StoresRegister): number {
  return onHand(item) - reserved(item.sku, register);
}

/** Ordered and not yet received. A promise from a supplier, never added to stock. */
export function onOrder(sku: string, register: StoresRegister): number {
  let n = 0;
  for (const o of register.orders) {
    if (o.status === "cancelled" || o.status === "received") continue;
    for (const l of o.lines) if (l.sku === sku) n += Math.max(0, l.qty - l.received);
  }
  return n;
}

/** The soonest a line on order is expected; null when nothing is coming. */
export function expectedAt(sku: string, register: StoresRegister): string | null {
  let soonest: string | null = null;
  for (const o of register.orders) {
    if (o.status === "cancelled" || o.status === "received" || !o.expectedAt) continue;
    const owing = o.lines.some((l) => l.sku === sku && l.received < l.qty);
    if (!owing) continue;
    if (soonest === null || (at(o.expectedAt) ?? 0) < (at(soonest) ?? 0)) soonest = o.expectedAt;
  }
  return soonest;
}

/** What was drawn from the shelf in the last `days`, as a positive quantity. */
export function consumed(sku: string, register: StoresRegister, days: number, now: Date): number {
  const from = now.getTime() - days * DAY;
  let n = 0;
  for (const m of register.movements) {
    if (m.sku !== sku) continue;
    const ms = at(m.at);
    if (ms === null || ms < from || ms > now.getTime()) continue;
    /* an issue leaves, a return comes back: the net is what the year actually used */
    if (m.kind === "issue") n += -Math.min(0, m.qty);
    else if (m.kind === "return") n -= Math.max(0, m.qty);
  }
  return Math.max(0, n);
}

/**
 * How long the shelf lasts at the rate of the last year.
 *
 * `null` when nothing has ever been drawn — see the header. A year is used
 * rather than a month because airfield consumption is seasonal: lamps go in
 * winter storms, paint in the marking season, and a thirty-day rate taken in
 * the wrong month plans the whole year wrong in both directions.
 */
export function daysOfCover(item: StockItem, register: StoresRegister, now: Date): number | null {
  const year = consumed(item.sku, register, 365, now);
  if (year <= 0) return null;
  /*
   * Floored at nothing. A line promised to more jobs than it can supply has a
   * NEGATIVE available, and dividing that by a usage rate produces a negative
   * number of days — which a screen renders as "-365 days of cover" and a
   * reader cannot interpret at all. The shelf is empty; the cover is zero, and
   * the over-commitment is reported by `available` where it belongs.
   */
  const have = Math.max(0, available(item, register));
  return Math.floor((have / (year / 365)) * 10) / 10;
}

export type StockState = "out" | "below_min" | "at_min" | "ok" | "over_max" | "expired";

/**
 * The state of one line, worst first.
 *
 * Read against AVAILABLE rather than on hand, because a part entirely
 * reserved is not available to the next job however full the bin looks.
 */
export function stateOf(item: StockItem, register: StoresRegister, now: Date): StockState {
  const exp = daysUntil(item.expiresAt, now);
  if (exp !== null && exp < 0) return "expired";
  const a = available(item, register);
  if (a <= 0) return "out";
  if (a < item.min) return "below_min";
  if (a === item.min) return "at_min";
  if (item.max > 0 && onHand(item) > item.max) return "over_max";
  return "ok";
}

export const STATE_ORDER: StockState[] = ["out", "expired", "below_min", "at_min", "over_max", "ok"];

export const STATE_LABEL: Record<StockState, string> = {
  out: "Out of stock",
  expired: "Past its shelf life",
  below_min: "Below reorder point",
  at_min: "At reorder point",
  over_max: "Above maximum",
  ok: "In stock",
};

/** Needs buying: out, expired or below the point, and not already fully covered by an order. */
export function needsReorder(item: StockItem, register: StoresRegister, now: Date): boolean {
  const s = stateOf(item, register, now);
  if (s !== "out" && s !== "below_min" && s !== "at_min" && s !== "expired") return false;
  return reorderQty(item, register, now) > 0;
}

/**
 * How many to buy: back to the maximum, less what is already coming.
 *
 * An expired line is replaced in full — what is on the shelf cannot be used,
 * so the available figure would flatter the order.
 */
export function reorderQty(item: StockItem, register: StoresRegister, now: Date): number {
  const expired = stateOf(item, register, now) === "expired";
  const have = expired ? 0 : available(item, register);
  const target = Math.max(item.max, item.min);
  return Math.max(0, target - have - onOrder(item.sku, register));
}

/** What the shelf is worth, at the last cost known for each line. */
export function valueOf(item: StockItem): number | null {
  return item.unitCost === undefined ? null : onHand(item) * item.unitCost;
}

/** Nothing drawn in a year and something still on the shelf: money standing still. */
export function isObsolete(item: StockItem, register: StoresRegister, now: Date): boolean {
  return onHand(item) > 0 && consumed(item.sku, register, 365, now) === 0;
}

export interface StockRow {
  item: StockItem;
  onHand: number;
  reserved: number;
  available: number;
  onOrder: number;
  expectedAt: string | null;
  state: StockState;
  cover: number | null;
  consumed30: number;
  consumed90: number;
  consumed365: number;
  value: number | null;
  reorder: number;
  obsolete: boolean;
  /** open work orders waiting on this line, by number */
  waiting: string[];
}

export function rowOf(item: StockItem, register: StoresRegister, now: Date): StockRow {
  return {
    item,
    onHand: onHand(item),
    reserved: reserved(item.sku, register),
    available: available(item, register),
    onOrder: onOrder(item.sku, register),
    expectedAt: expectedAt(item.sku, register),
    state: stateOf(item, register, now),
    cover: daysOfCover(item, register, now),
    consumed30: consumed(item.sku, register, 30, now),
    consumed90: consumed(item.sku, register, 90, now),
    consumed365: consumed(item.sku, register, 365, now),
    value: valueOf(item),
    reorder: reorderQty(item, register, now),
    obsolete: isObsolete(item, register, now),
    waiting: register.reservations
      .filter((r) => r.sku === item.sku && isOutstanding(r))
      .map((r) => r.workOrder),
  };
}

/** Every working line, worst state first, then the soonest to run out. */
export function rows(register: StoresRegister, now: Date): StockRow[] {
  const out = register.items.filter((i) => !i.obsoleted).map((i) => rowOf(i, register, now));
  out.sort((a, b) => {
    const s = STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state);
    if (s !== 0) return s;
    const ac = a.cover ?? Number.POSITIVE_INFINITY;
    const bc = b.cover ?? Number.POSITIVE_INFINITY;
    if (ac !== bc) return ac - bc;
    return a.item.sku.localeCompare(b.item.sku);
  });
  return out;
}

/* ── what the register says as a whole ────────────────────────────────── */

export interface StoresSummary {
  lines: number;
  categories: number;
  value: number | null;
  /** lines whose value could not be computed because no cost is recorded */
  valueUnknown: number;
  out: number;
  belowMin: number;
  expired: number;
  obsolete: number;
  criticalHeld: number;
  criticalTotal: number;
  /** median days of cover across the lines that have a rate; null when none do */
  medianCover: number | null;
  coverUnknown: number;
  reservedLines: number;
  waitingWorkOrders: string[];
  onOrderLines: number;
  overdueOrders: number;
}

export function summarise(register: StoresRegister, now: Date): StoresSummary {
  const list = rows(register, now);
  const covers = list.map((r) => r.cover).filter((c): c is number => c !== null).sort((a, b) => a - b);
  const values = list.map((r) => r.value);
  const known = values.filter((v): v is number => v !== null);
  const critical = list.filter((r) => r.item.critical);
  const waiting = new Set<string>();
  for (const r of list) for (const w of r.waiting) waiting.add(w);
  let overdue = 0;
  for (const o of register.orders) {
    if (o.status !== "open" && o.status !== "part_received") continue;
    const d = daysUntil(o.expectedAt, now);
    if (d !== null && d < 0) overdue += 1;
  }
  return {
    lines: list.length,
    categories: new Set(list.map((r) => r.item.category)).size,
    value: known.length > 0 ? known.reduce((a, b) => a + b, 0) : null,
    valueUnknown: values.length - known.length,
    out: list.filter((r) => r.state === "out").length,
    belowMin: list.filter((r) => r.state === "below_min" || r.state === "at_min").length,
    expired: list.filter((r) => r.state === "expired").length,
    obsolete: list.filter((r) => r.obsolete).length,
    criticalHeld: critical.filter((r) => r.available > 0 && r.state !== "expired").length,
    criticalTotal: critical.length,
    medianCover: covers.length > 0 ? covers[Math.floor((covers.length - 1) / 2)] : null,
    coverUnknown: list.length - covers.length,
    reservedLines: list.filter((r) => r.reserved > 0).length,
    waitingWorkOrders: [...waiting].sort(),
    onOrderLines: list.filter((r) => r.onOrder > 0).length,
    overdueOrders: overdue,
  };
}

/**
 * How well the stores filled what was asked of them, over a window.
 *
 * Counted from the movements: an issue filled from stock against a
 * reservation that had to wait. A fill rate with nothing asked of it is
 * `null` — not 100%, which is what an empty store scores on every system that
 * gets this wrong.
 */
export function fillRate(register: StoresRegister, now: Date, days = 90): { pct: number | null; asked: number; filled: number } {
  const from = now.getTime() - days * DAY;
  let asked = 0;
  let filled = 0;
  for (const r of register.reservations) {
    const ms = at(r.at);
    if (ms === null || ms < from) continue;
    asked += 1;
    if (r.issued >= r.qty) filled += 1;
  }
  return { pct: asked === 0 ? null : Math.round((filled / asked) * 100), asked, filled };
}

/**
 * Stock count accuracy: of the lines counted in the window, how many were
 * right. A line never counted is not evidence of accuracy in either
 * direction, so it is excluded and reported separately.
 */
export function countAccuracy(register: StoresRegister, now: Date, days = 365): { pct: number | null; counted: number; uncounted: number } {
  const from = now.getTime() - days * DAY;
  let counted = 0;
  let exact = 0;
  let uncounted = 0;
  for (const i of register.items) {
    if (i.obsoleted) continue;
    const ms = at(i.counted?.at);
    if (ms === null || ms < from) {
      uncounted += 1;
      continue;
    }
    counted += 1;
    if ((i.counted?.variance ?? 0) === 0) exact += 1;
  }
  return { pct: counted === 0 ? null : Math.round((exact / counted) * 100), counted, uncounted };
}

/* ── the replenishment run ────────────────────────────────────────────── */

export interface ReorderLine {
  item: StockItem;
  qty: number;
  state: StockState;
  cover: number | null;
  estimatedCost: number | null;
  supplier: string;
  waiting: string[];
  /** in this many days, at the current rate, the shelf is empty; null when unknown */
  emptyInDays: number | null;
  /**
   * The requisition already asking for this line, where there is one.
   *
   * A replenishment run that cannot see what it asked for last time raises the
   * same requisition again every time somebody presses the button — and a
   * person who presses it twice because nothing visibly happened ends up with
   * two of everything in front of the same approver.
   */
  requested?: string;
}

/** The requisition already asking for a line, if one is waiting or approved. */
export function requestedOn(sku: string, register: StoresRegister): string | undefined {
  return register.requisitions.find(
    (q) => (q.status === "submitted" || q.status === "approved") && q.lines.some((l) => l.sku === sku),
  )?.number;
}

export const NO_SUPPLIER = "No supplier recorded";

/**
 * What to buy now, grouped by supplier, worst first.
 *
 * Grouped by supplier because that is the shape a purchase order takes: ten
 * lines from one supplier is one order, one delivery and one invoice, and a
 * system that raises ten orders instead has cost the airport nine times the
 * paperwork to save nobody anything.
 */
export function reorderRun(register: StoresRegister, now: Date): { supplier: string; lines: ReorderLine[]; total: number | null }[] {
  const bySupplier = new Map<string, ReorderLine[]>();
  for (const r of rows(register, now)) {
    if (!needsReorder(r.item, register, now)) continue;
    const qty = r.reorder;
    const rate = r.consumed365 / 365;
    const line: ReorderLine = {
      item: r.item,
      qty,
      state: r.state,
      cover: r.cover,
      estimatedCost: r.item.unitCost === undefined ? null : r.item.unitCost * qty,
      supplier: r.item.supplier ?? NO_SUPPLIER,
      waiting: r.waiting,
      /* floored at nothing, for the reason in daysOfCover: an empty shelf is
       * empty in zero days, never in a negative number of them */
      emptyInDays: rate > 0 ? Math.floor(Math.max(0, r.available) / rate) : null,
      requested: requestedOn(r.item.sku, register),
    };
    const list = bySupplier.get(line.supplier) ?? [];
    list.push(line);
    bySupplier.set(line.supplier, list);
  }
  const out = [...bySupplier.entries()].map(([supplier, lines]) => {
    lines.sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state));
    const costs = lines.map((l) => l.estimatedCost);
    const known = costs.filter((c): c is number => c !== null);
    return { supplier, lines, total: known.length === costs.length ? known.reduce((a, b) => a + b, 0) : null };
  });
  /* the supplier with the most urgent line first, so the first order raised is the one that matters */
  out.sort((a, b) => STATE_ORDER.indexOf(a.lines[0].state) - STATE_ORDER.indexOf(b.lines[0].state));
  return out;
}

/**
 * Whether an order placed today arrives before the shelf empties.
 *
 * This is the sentence a reorder report exists to produce, and almost no
 * inventory system says it out loud: ordering on the reorder point is only
 * safe if the lead time is shorter than the cover, and when it is not, the
 * reorder point itself is wrong.
 */
export function arrivesInTime(line: ReorderLine): { inTime: boolean | null; shortBy: number | null } {
  const lead = line.item.leadDays;
  if (lead === undefined || line.emptyInDays === null) return { inTime: null, shortBy: null };
  const short = lead - line.emptyInDays;
  return { inTime: short <= 0, shortBy: short > 0 ? short : null };
}

/* ── parts against a job ──────────────────────────────────────────────── */

export interface WorkOrderParts {
  workOrder: string;
  lines: {
    reservation: Reservation;
    item: StockItem | null;
    outstanding: number;
    availableNow: number;
    expectedAt: string | null;
  }[];
  /** nothing outstanding: every part is in the technician's hands */
  complete: boolean;
  /** outstanding lines with nothing on the shelf — the job cannot proceed */
  short: number;
  /** the latest date any outstanding line is expected; null when something has no date at all */
  readyAt: string | null;
  /** true when a line is short and nothing is on order for it */
  blocked: boolean;
}

/** Everything the stores owe one work order, and whether it can start. */
export function partsFor(workOrder: string, register: StoresRegister, now: Date): WorkOrderParts {
  const lines = register.reservations
    .filter((r) => r.workOrder === workOrder && !r.released)
    .map((reservation) => {
      const item = register.items.find((i) => i.sku === reservation.sku) ?? null;
      /*
       * Stock past its shelf life is not available to a job. Six drums of
       * marking paint that expired last month are on the shelf, counted and
       * valued — and a repaint crew cannot open one of them. A parts list that
       * shows them as available sends the crew out and the job fails at the
       * tin, which is later and more expensive than failing here.
       */
      const usable = item && stateOf(item, register, now) !== "expired" ? onHand(item) : 0;
      return {
        reservation,
        item,
        outstanding: Math.max(0, reservation.qty - reservation.issued),
        availableNow: usable,
        expectedAt: expectedAt(reservation.sku, register),
      };
    });
  const outstanding = lines.filter((l) => l.outstanding > 0);
  const short = outstanding.filter((l) => l.availableNow < l.outstanding);
  let readyAt: string | null = null;
  let unknown = false;
  for (const l of short) {
    if (!l.expectedAt) {
      unknown = true;
      continue;
    }
    if (readyAt === null || (at(l.expectedAt) ?? 0) > (at(readyAt) ?? 0)) readyAt = l.expectedAt;
  }
  return {
    workOrder,
    lines,
    complete: outstanding.length === 0,
    short: short.length,
    readyAt: unknown ? null : readyAt,
    blocked: short.some((l) => !l.expectedAt),
  };
}

/** One line of plain English about a job's parts, for the work-order screen. */
export function partsSentence(parts: WorkOrderParts, now: Date): string {
  if (parts.lines.length === 0) return t("No parts are reserved against this job.");
  if (parts.complete) return t("Every part is issued.");
  if (parts.short === 0) {
    return tn(
      parts.lines.filter((l) => l.outstanding > 0).length,
      "{n} part is on the shelf, reserved and not yet collected.",
      "{n} parts are on the shelf, reserved and not yet collected.",
    );
  }
  if (parts.blocked) {
    return tn(
      parts.short,
      "{n} part is short and nothing is on order for it.",
      "{n} parts are short and nothing is on order for them.",
    );
  }
  const days = daysUntil(parts.readyAt ?? undefined, now);
  if (days === null) return tn(parts.short, "{n} part is short.", "{n} parts are short.");
  if (days < 0) {
    return tn(
      Math.abs(days),
      "The last part was due {n} day ago and has not arrived.",
      "The last part was due {n} days ago and has not arrived.",
    );
  }
  /* "in 0 days" is not something a planner says, and it is the one day that matters most */
  if (days === 0) return t("The last part is due today.");
  return tn(days, "The job can start in {n} day, when the last part is due.", "The job can start in {n} days, when the last part is due.");
}

/* ── the proposed targets ─────────────────────────────────────────────── */

/**
 * Targets for the stores, every one of them PROPOSED.
 *
 * These are the figures the executive Logistics board grades against. No
 * operator has agreed them; they are put forward as a starting point and
 * marked as such wherever they are shown, in the same way as every other
 * target on this platform. An unagreed number presented as a standard is how
 * a benchmark becomes a stick.
 */
export interface StoresTarget {
  key: string;
  label: string;
  target: number;
  unit: string;
  dir: "up" | "down";
  proposed: true;
}

export const STORES_TARGETS: StoresTarget[] = [
  { key: "availability", label: "Lines in stock", target: 98, unit: "%", dir: "up", proposed: true },
  { key: "stockouts", label: "Lines out of stock", target: 0, unit: "lines", dir: "down", proposed: true },
  { key: "belowmin", label: "Lines below reorder point", target: 5, unit: "lines", dir: "down", proposed: true },
  { key: "cover", label: "Days of cover, median", target: 45, unit: "days", dir: "up", proposed: true },
  { key: "critical", label: "Critical spares held", target: 100, unit: "%", dir: "up", proposed: true },
  { key: "fulfil", label: "Issues filled from stock", target: 95, unit: "%", dir: "up", proposed: true },
  { key: "accuracy", label: "Stock count accuracy", target: 98, unit: "%", dir: "up", proposed: true },
];


/* ── what a scan means ────────────────────────────────────────────────── */

export type Scanned =
  | { kind: "item"; item: StockItem; by: "sku" | "barcode" | "part number" }
  | { kind: "workOrder"; number: string }
  | { kind: "location"; location: StoreLocation }
  | { kind: "unknown"; code: string };

/**
 * Resolve a scan, or something somebody typed, to whatever it names.
 *
 * A stores counter takes one input and one input only — the thing in front of
 * the storekeeper — and works out what it is. A catalogue number, a barcode
 * stuck to a bin, a manufacturer's part number, the work order on the
 * technician's docket, or the code of a store.
 *
 * The order matters. A part is looked for first and a work order only after,
 * because a catalogue number that happens to look like a job number is still a
 * part, and handing somebody the wrong thing is worse than making them type.
 *
 * A work order is recognised by its SHAPE rather than by being found: this
 * console holds a sample of the airport's jobs, the maintenance system holds
 * the rest, and refusing to issue against a real job because this register has
 * never heard of it would stop the counter working on its first day.
 */
export function lookupCode(raw: string, register: StoresRegister): Scanned {
  const code = raw.trim();
  if (!code) return { kind: "unknown", code };
  const same = (a: string | undefined, b: string) => (a ?? "").trim().toLowerCase() === b.toLowerCase();

  const working = register.items.filter((i) => !i.obsoleted);
  const bySku = working.find((i) => same(i.sku, code));
  if (bySku) return { kind: "item", item: bySku, by: "sku" };
  const byBarcode = working.find((i) => i.barcode && same(i.barcode, code));
  if (byBarcode) return { kind: "item", item: byBarcode, by: "barcode" };
  const byPart = working.find((i) => i.partNumber && same(i.partNumber, code));
  if (byPart) return { kind: "item", item: byPart, by: "part number" };

  if (/^WO[-\s]?\d{3,}$/i.test(code)) {
    return { kind: "workOrder", number: code.toUpperCase().replace(/\s+/, "-") };
  }
  const loc = register.locations.find((l) => same(l.code, code));
  if (loc) return { kind: "location", location: loc };

  return { kind: "unknown", code };
}